import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Auth from "../Auth";
import { activeSprintWindow } from "../../../src/shared/novelNovemberSprint";

const estLocal = value => Number.isFinite(Date.parse(value)) ? new Date(Date.parse(value) - 5 * 3600000).toISOString().slice(0, 16) : "";
const fromEST = value => value ? `${value}:00-05:00` : "";

const SprintPanel = styled.section`
  box-sizing: border-box;
  display: grid;
  gap: 16px;
  width: 100%;
  max-width: 1100px;
  padding: 24px;
  color: #252638;
  background: #fff;
  text-align: left;

  h1 { margin: 0; font-size: 26px; font-weight: 600; line-height: 1.3; }
  h2 { margin: 16px 0 0; font-size: 19px; font-weight: 600; line-height: 1.4; }
  p { margin: 0; font-size: 14px; line-height: 1.6; }
  [role="status"]:empty { display: none; }
  .sprint-status { display: grid; gap: 10px; padding: 16px; border: 1px solid #d7d7e2; border-radius: 8px; background: #f7f6fb; }
  fieldset { display: grid; gap: 16px; min-width: 0; padding: 0; border: 0; }
  legend { margin-bottom: 12px; font-size: 18px; font-weight: 600; }
  label { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 14px; line-height: 1.5; }
  input { box-sizing: border-box; max-width: 100%; padding: 8px; border: 1px solid #b8bac8; border-radius: 6px; background: #fff; color: #252638; font: inherit; }
  input[type="number"] { width: 100px; min-height: 38px; }
  input[type="checkbox"] { appearance: auto; width: 16px; height: 16px; accent-color: #6652c9; }
  input:focus-visible { outline: 2px solid #6652c9; outline-offset: 2px; }
  .sprint-actions { display: flex; flex-wrap: wrap; gap: 12px; }
  .sprint-windows { max-width: 100%; overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  th, td { padding: 10px; border-bottom: 1px solid #e2e1eb; text-align: left; }
  th { font-weight: 600; }
  input[type="datetime-local"] { width: 100%; min-width: 210px; }

  @media (max-width: 600px) { padding: 16px; }
`;

// The marker opts out of section#container's defaults. Match #admin's ID and
// exceed its :not(...) specificity without changing buttons on other pages.
const SprintButton = styled.button.attrs({ className: "use-component-styles", type: "button" })`
  &, #admin &&& {
    min-height: 38px;
    padding: 9px 14px;
    border: 1px solid #b8bac8;
    border-radius: 6px;
    background: #fff;
    color: #41338f;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    cursor: pointer;

    &:hover:not(:disabled) { background: #ede9ff; border-color: #8b7ae1; }
    &:focus-visible { outline: 2px solid #6652c9; outline-offset: 2px; }
    &:disabled { background: #f3f3f7; color: #626175; border-color: #d7d7e2; opacity: 1; cursor: default; }
  }
`;

export default function NovelNovemberSprint() {
  const [config, setConfig] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [minutes, setMinutes] = useState(180);
  const [now, setNow] = useState(Date.now());
  const clockOffset = useRef(0);
  const alive = useRef(true);
  const inFlight = useRef(false);
  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    Auth.fetchWithIdToken("/api/admin/novel-november/get", {}).then(result => {
      if (!cancelled) {
        if (Number.isFinite(result.now)) clockOffset.current = result.now - Date.now();
        setNow(Date.now() + clockOffset.current);
        setConfig(result.config); setSaved(result.config);
      }
    }).catch(() => { if (!cancelled) setMessage("Unable to load sprint settings. Reload to retry."); });
    const clock = setInterval(() => setNow(Date.now() + clockOffset.current), 1000);
    return () => { cancelled = true; alive.current = false; clearInterval(clock); };
  }, []);
  const save = async (next, startManualMinutes) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setMessage("");
    try {
      const result = await Auth.fetchWithIdToken("/api/admin/novel-november/save", { config: next, ...(startManualMinutes !== undefined && { startManualMinutes }) });
      if (!alive.current) return;
      if (result.conflict) throw new Error("Settings changed in another session. Reload before saving; your edits remain here.");
      if (!result.config) throw new Error(result.message || "Unable to save. Reload to check whether it completed.");
      if (Number.isFinite(result.now)) clockOffset.current = result.now - Date.now();
      setNow(Date.now() + clockOffset.current);
      setConfig(result.config); setSaved(result.config); setMessage("Saved.");
    } catch (error) {
      if (alive.current) setMessage(error.message || "Unable to save. Reload to check whether it completed.");
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const editWindow = (index, changes) => setConfig(current => ({ ...current, windows: current.windows.map((window, i) => i === index ? { ...window, ...changes } : window) }));
  const dirty = config && JSON.stringify(config) !== JSON.stringify(saved);
  const active = activeSprintWindow(saved, now);
  const next = saved?.enabled && saved.windows.filter(window => window.enabled && Date.parse(window.start) > now)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start))[0];
  return <SprintPanel aria-label="Novel November sprints">
    <h1>Novel November 2026 sprints</h1>
    <p>Enroll participants in Admin → Gates → Novel November 2026 preregistrant.</p>
    <p>All times use EST (UTC−5).</p>
    <p role="status" aria-live="polite">{message}</p>
    {saved && <div className="sprint-status" aria-label="Saved sprint status">
      <strong>{!saved.enabled ? "Paused"
        : active ? `Active until ${estLocal(active.end)} EST`
        : next ? `Enabled · next sprint ${estLocal(next.start)} EST`
        : "Enabled · no upcoming windows"}</strong>
      <p>Saved cap: {saved.maxConcurrent ?? "not set"}. Save settings to apply changes.</p>
      {dirty && <p>Unsaved changes</p>}
    </div>}
    {!config ? <p>Loading settings…</p> : <fieldset disabled={busy}>
      <legend>Sprint controls</legend>
      <label><input type="checkbox" checked={config.enabled} onChange={event => setConfig({ ...config, enabled: event.target.checked })} /> Enable sprints</label>
      <label>Global concurrency cap <input type="number" min="1" max="10000" value={config.maxConcurrent ?? ""} onChange={event => setConfig({ ...config, maxConcurrent: event.target.value ? Number(event.target.value) : null })} /></label>
      <p>Shared by all participants. Each Write card uses one slot; each Draft analysis, Quick Fix or generation uses one. One request per account.</p>
      <div className="sprint-actions">
        <SprintButton disabled={!dirty} onClick={() => save(config)}>Save settings</SprintButton>
        <SprintButton disabled={!dirty} onClick={() => { setConfig(saved); setMessage(""); }}>Cancel edits</SprintButton>
        <SprintButton disabled={dirty || !config.enabled} onClick={() => save({ ...config, enabled: false, manual: null })}>Pause all sprints</SprintButton>
      </div>
      <h2>Surprise sprint</h2>
      <p>{config.manual ? `${estLocal(config.manual.start)} to ${estLocal(config.manual.end)} EST` : "No surprise sprint."}</p>
      <label>Duration (minutes) <input type="number" min="1" max="1440" value={minutes} onChange={event => setMinutes(Number(event.target.value))} /></label>
      <div className="sprint-actions">
        <SprintButton disabled={dirty || !config.enabled || !Number.isInteger(minutes) || minutes < 1 || minutes > 1440} onClick={() => save(config, minutes)}>Start surprise sprint now</SprintButton>
        <SprintButton disabled={dirty || !config.manual} onClick={() => save({ ...config, manual: null })}>Stop surprise sprint</SprintButton>
      </div>
      <p>Stop ends only the surprise window. Pause all sprints stops new scheduled access too.</p>
      <h2>Scheduled windows</h2>
      <div className="sprint-windows"><table><thead><tr><th>Enabled</th><th>Start (EST)</th><th>End (EST)</th></tr></thead><tbody>
        {config.windows.map((window, index) => <tr key={window.id}>
          <td><input aria-label={`Enable ${window.id}`} type="checkbox" checked={window.enabled} onChange={event => editWindow(index, { enabled: event.target.checked })} /></td>
          <td><input aria-label={`Start ${window.id}`} type="datetime-local" value={estLocal(window.start)} onChange={event => editWindow(index, { start: fromEST(event.target.value) })} /></td>
          <td><input aria-label={`End ${window.id}`} type="datetime-local" value={estLocal(window.end)} onChange={event => editWindow(index, { end: fromEST(event.target.value) })} /></td>
        </tr>)}
      </tbody></table></div>
    </fieldset>}
  </SprintPanel>;
}
